import React from 'react';
import clsx from 'clsx';
import { TypographyStyles } from 'src/styles';
import { formatDateRange } from 'src/helpers/date-helpers';
import { getScoreStyleColor } from 'src/helpers/color-helper';
import { Tooltip } from '../../components/uikit/tooltip/tooltip.component';
import { ReactComponent as Doctor } from '../../assets/img/survey/doctor_16.svg';
import placeholderIcon from '../../assets/img/survey_placeholder.svg';
import { useScopedTranslation } from '../../application/localisation/useScopedTranslation';
import { Loading } from 'uikit/suspense/Loading';
import { SurveyAnswersProps } from './surveyAnswers';
import { SurveyAnswersData } from './useSurveyAnswersData';
import { AnswersTableRow } from './answerTableRow.component';
import { ScoreTableRow } from './scoreTableRow.component';
import { HeaderTableRow } from './headerTableRow.component';
import Style from './surveyAnswers.module.css';

type SurveyAnswersHeaderProps = Pick<SurveyAnswersData, 'surveysData' | 'answersList'> &
  Pick<SurveyAnswersProps, 'answerId'>;

export const SurveyAnswersHeader = ({ surveysData, answersList, answerId }: SurveyAnswersHeaderProps) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.SurveySection');
  let headerText = '';
  if (surveysData) {
    headerText =
      answerId && surveysData.answers
        ? t('LastSurveyModal.ModalHeader', {
            date: formatDateRange(surveysData.answers[0].answerDate, surveysData.answers[0].answerDate),
          })
        : t('StatisticsModal.Header', {
            period: surveysData.studyStartedAt
              ? formatDateRange(surveysData.studyStartedAt, surveysData.studyFinishedAt)
              : '',
          });
  }

  const answeredBy = answersList?.[0]?.answeredBy;

  return (
    <div className={Style.modalHeader} data-test-id={'study-period-date'}>
      <span className={TypographyStyles.plainText14}>{headerText}</span>
      {answerId && answeredBy && (
        <Tooltip hostStyles={Style.doctorIcon} text={t('LastAvailableStats.FilledBy', { doctor: answeredBy.fullName })}>
          <Doctor />
        </Tooltip>
      )}
    </div>
  );
};

export const SurveyAnswersLegend = ({ legend }: { legend: string }) => (
  <div className={Style.modalFooter}>
    {legend.split(',').map((value, index) => (
      <span key={index}>{value}</span>
    ))}
  </div>
);

type SurveyAnswersTableProps = Pick<
  SurveyAnswersData,
  'answersList' | 'answersStatisticsList' | 'questionsList' | 'isLoading'
> &
  Pick<SurveyAnswersProps, 'answerId'> & {
    swipeIndex: number;
    onSwipe: (index: number) => void;
  };

export const SurveyAnswersTable = ({
  answersList,
  answersStatisticsList,
  questionsList,
  isLoading,
  answerId,
  swipeIndex,
  onSwipe,
}: SurveyAnswersTableProps) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.SurveySection');

  if (!answersStatisticsList || !questionsList || !answersList) {
    return (
      <Loading
        loading={isLoading}
        renderChildren={false}
        testId={'survey-results'}
        containerClassName={Style.placeholderContainer}
      >
        <img className={Style.placeholderIcon} alt={'survey placeholder icon'} src={placeholderIcon} />
        <p className={clsx(TypographyStyles.paragraph14, Style.placeholderText)}>{t('LastAvailableStats.NoDataYet')}</p>
      </Loading>
    );
  }

  const answerSchemes = answersList.map((answer) => answer?.answerScheme ?? null);
  const dates = answersList.map((answer) => ({ date: answer?.date, answeredBy: answer?.answeredBy }));
  const scores = answersList.map((answer) =>
    answer
      ? {
          score: answer.score,
          quality: answer.quality,
          scoreStatus: answer.scoreStatus,
          answerId: answer.answerId,
          canRetryScoreCalculation: answer.canRetryScoreCalculation,
        }
      : null,
  );

  return (
    <>
      {!answerId && (
        <div className={Style.tableHeader}>
          <HeaderTableRow
            data={dates}
            onSwipe={onSwipe}
            swipeIndex={swipeIndex}
            isSingleAnswer={answerId !== undefined}
          />
        </div>
      )}
      <div className={Style.rows}>
        {questionsList.map((question, index) =>
          question ? (
            <div key={index}>
              <AnswersTableRow
                index={index + 1}
                parentQuestion={undefined}
                question={question}
                answers={answerSchemes}
                swipeIndex={swipeIndex}
                viewMode={answerId === undefined ? 'table' : 'single'}
              />
            </div>
          ) : (
            <React.Fragment key={index} />
          ),
        )}
      </div>
      <div className={Style.tableFooter}>
        <ScoreTableRow
          text={t('LastAvailableStats.Score')}
          scores={scores}
          swipeIndex={swipeIndex}
          containerClassName={clsx({ [getScoreStyleColor(scores[0]?.quality)]: answerId })}
          isSingleAnswer={answerId !== undefined}
        />
      </div>
    </>
  );
};
